/**
 * The in-page address attestation gate — the one control that stands between a
 * hostile response body and a payer sending funds to the wrong address.
 *
 * ## Why this exists
 *
 * A public checkout page is a phishing target with a short path to real loss:
 * whoever controls the bytes the page renders controls the deposit address, and
 * an address swap is indistinguishable from a correct one at a glance. The
 * spec's rule (PAY-00, spec lines 74-76 and 240-245) is therefore absolute:
 *
 *   "The address is displayed only after the page verifies a server-signed
 *    address attestation … On attestation failure the address is withheld and
 *    the payer is told *'We can't verify this payment address. Do not pay.'*"
 *
 * This module is that verification, and it is **fail-closed by construction**:
 * every path that is not a completed, bound, in-date signature check over a key
 * pinned out of band returns `{ok: false}`. There is no "assume good" branch, no
 * timeout that proceeds, and no fallback to the unverified address.
 *
 * Three properties carry the weight, and each is a unit test:
 *
 *   1. **The key is never taken from the response.** The response names a
 *      `keyId`; the public key comes only from the pinned trust root below. An
 *      attacker who swaps the address can therefore also swap `keyId`, and it
 *      buys nothing — the substituted key is not in the root. (A design that
 *      accepted `publicKey` from the body would verify perfectly and be
 *      worthless.)
 *   2. **The signature is checked over a payload bound to what is displayed.**
 *      The signed bytes must name the same address *and* the same network the
 *      page is about to show, so an attestation the issuer legitimately signed
 *      for another invoice, another network or another payer cannot be replayed
 *      here. This closes the obvious hole in "verify a signature": a genuine
 *      signature over the wrong thing.
 *   3. **The key material is absent, so nothing is displayed.** The pinned root
 *      is empty in this build (see `PINNED_ATTESTATION_KEYS`), so every address
 *      response is refused and the payer sees the refusal copy. That is the
 *      correct state, not a gap: the alternative — inventing a key, or trusting
 *      one the server sent — would render an unverified address, which is
 *      precisely the `address_shown_without_attestation` failure the page spec
 *      lists as a bug signal.
 *
 * ## What this module is NOT
 *
 * It is not a defence against a compromised issuer signing key, and it is not a
 * substitute for TLS. It answers one question — "was this exact address signed
 * by the key I already trust, for this invoice on this network?" — and refuses
 * everything else.
 *
 * ## Provenance of the shape
 *
 * The contract pack for this surface (`contracts/invoicing-pay.openapi.yaml`,
 * BEHAVIOUR-paylink) is not present in this checkout, so the *field names* of
 * the attestation object are taken from the spec's prose ("server-signed",
 * "verified by the checkout before display") and are marked provisional. The
 * *semantics* implemented here — pinned key, bound payload, expiry, fail
 * closed — are the spec's, and are the part the tests pin.
 */

import { displayText, type PayAddressAttestationWire, type PayAddressView } from "./pay-types";

/**
 * The signing key for a `keyId`, as pinned in the shipped bundle.
 *
 * `publicKey` is base64 of the raw 32-byte Ed25519 public key. It is public
 * material by definition; what matters is that it arrives out of band (review,
 * release artefact) rather than over the same connection as the address.
 */
export interface PinnedAttestationKey {
  algorithm: "Ed25519";
  publicKey: string;
}

export type AttestationTrustRoot = Readonly<Record<string, PinnedAttestationKey>>;

/**
 * Trust root for the checkout's address attestations.
 *
 * **Empty on purpose.** The issuer-side key that signs these attestations is
 * provisioned with the pay contract (`context#2027`); until that key is issued
 * and pinned here through review, no address can pass this gate, and the page
 * therefore shows the refusal state. Filling this in is a one-line, reviewable
 * change with a test that flips the "refuses" assertion — which is exactly the
 * property wanted: shipping an unverifiable address must require a deliberate
 * edit to a file whose whole purpose is to prevent it.
 */
export const PINNED_ATTESTATION_KEYS: AttestationTrustRoot = Object.freeze({});

/** Why an address was withheld. Rendered as the bounded reason code, verbatim. */
export type AttestationRefusal =
  | "NO_ATTESTATION"
  | "NO_TRUST_ROOT"
  | "UNKNOWN_KEY"
  | "MALFORMED_ATTESTATION"
  | "UNSUPPORTED_ALGORITHM"
  | "PAYLOAD_UNREADABLE"
  | "BINDING_MISMATCH"
  | "EXPIRED"
  | "CRYPTO_UNAVAILABLE"
  | "SIGNATURE_INVALID";

export interface AttestedAddress {
  /** The verified address, safe to display. */
  address: string;
  /** Display-only typo check; see `addressChecksum`. */
  checksum: string;
}

export type AttestationVerdict =
  | { ok: true; address: AttestedAddress; keyId: string }
  | { ok: false; reason: AttestationRefusal };

/* ------------------------------------------------------------ signature */

export interface SignatureVerifier {
  /**
   * True only when `signature` is a valid signature over **exactly** `payload`
   * bytes by the key `publicKeyBase64`. May reject; a rejection is a refusal,
   * never an "assume valid".
   */
  verify(args: {
    algorithm: string;
    publicKeyBase64: string;
    payload: Uint8Array;
    signature: Uint8Array;
  }): Promise<boolean>;
}

/** The payload the issuer signs, once decoded. Provisional field names. */
interface AttestationPayload {
  address: string;
  network: string;
  expiresAt: string;
}

function decodeBase64(value: string): Uint8Array | null {
  try {
    if (typeof atob === "function") {
      const binary = atob(value);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return bytes;
    }
  } catch {
    return null;
  }
  try {
    const buffer = (globalThis as { Buffer?: { from(v: string, e: string): Uint8Array } }).Buffer;
    if (buffer) return new Uint8Array(buffer.from(value, "base64"));
  } catch {
    /* fall through */
  }
  return null;
}

function decodeBase64Text(value: string, max: number): string | null {
  const bytes = decodeBase64(value);
  if (!bytes) return null;
  let text: string;
  try {
    text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
  return displayText(text, max);
}

/**
 * Ed25519 verification on WebCrypto. A runtime without `crypto.subtle` (and
 * every runtime that cannot import an Ed25519 key) is a refusal, not a bypass.
 */
export function createWebCryptoVerifier(subtle: SubtleCrypto | undefined): SignatureVerifier {
  return {
    async verify({ algorithm, publicKeyBase64, payload, signature }) {
      if (!subtle || algorithm !== "Ed25519") return false;
      const key = decodeBase64(publicKeyBase64);
      if (!key || key.length !== 32) return false;
      try {
        const cryptoKey = await subtle.importKey("raw", key as unknown as BufferSource, { name: "Ed25519" }, false, [
          "verify",
        ]);
        return await subtle.verify(
          { name: "Ed25519" },
          cryptoKey,
          signature as unknown as BufferSource,
          payload as unknown as BufferSource,
        );
      } catch {
        return false;
      }
    },
  };
}

function defaultVerifier(): SignatureVerifier {
  const subtle = (globalThis as { crypto?: Crypto }).crypto?.subtle;
  return createWebCryptoVerifier(subtle);
}

/* -------------------------------------------------------------- checksum */

/**
 * A short, stable, non-security checksum of an address, so the payer can
 * compare characters against their wallet before sending.
 *
 * This is a **display aid only**. It is computed in the page from the address
 * itself, so it can never detect a substituted address — the substituted address
 * gets its own, internally consistent, checksum. The control that detects
 * substitution is the signed attestation above; this exists because the spec
 * asks for a checksum to be shown beside the address, and pretending it is a
 * security boundary would be worse than saying so.
 *
 * FNV-1a over the UTF-8 bytes, rendered as two groups of four hex digits. It has
 * no dependencies and no async, so it cannot fail at render time.
 */
export function addressChecksum(address: string): string {
  let hash = 0x811c9dc5;
  const bytes = new TextEncoder().encode(address);
  for (const byte of bytes) {
    hash ^= byte;
    // 32-bit FNV prime (16777619) by shifts, kept in unsigned range.
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0;
  }
  const hex = hash.toString(16).toUpperCase().padStart(8, "0");
  return `${hex.slice(0, 4)}-${hex.slice(4)}`;
}

/* ----------------------------------------------------------------- gate */

export interface VerifyAddressInput {
  /** The address the service asked the page to display. */
  address: string;
  /** The network the address was requested for, as the page requested it. */
  network: string;
  /** The attestation from the address response, or null when none was sent. */
  attestation: PayAddressAttestationWire | null | undefined;
  trustRoot?: AttestationTrustRoot;
  verifier?: SignatureVerifier;
  /** Injectable clock, so expiry is testable without waiting. */
  now?: number;
}

/**
 * The gate. Returns `{ok: true}` only for a signature by a pinned key over a
 * payload that binds this address, this network and a future expiry.
 *
 * Every early return refuses. Nothing is widened for convenience, and the
 * caller (`deposit-address.tsx`) renders the address only in the `ok` arm.
 */
export async function verifyAddressAttestation(input: VerifyAddressInput): Promise<AttestationVerdict> {
  const trustRoot = input.trustRoot ?? PINNED_ATTESTATION_KEYS;
  const verifier = input.verifier ?? defaultVerifier();
  const now = input.now ?? Date.now();

  // 1. No attestation at all: the service sent an address with nothing to check.
  const attestation = input.attestation;
  if (!attestation) return { ok: false, reason: "NO_ATTESTATION" };

  // 2. Nothing pinned to check it against. Refuse *before* looking at anything
  //    the response supplied, so a hostile body cannot influence this branch.
  if (Object.keys(trustRoot).length === 0) return { ok: false, reason: "NO_TRUST_ROOT" };

  // 3. The response names a key; only the pinned root can say what that key is.
  const keyId = displayText(attestation.keyId, 128);
  const pinned = keyId ? trustRoot[keyId] : undefined;
  if (!keyId || !pinned) return { ok: false, reason: "UNKNOWN_KEY" };

  // 4. The algorithm must be the one pinned for that key — a body that asks for
  //    a different primitive does not get to choose one.
  if (attestation.algorithm !== pinned.algorithm) return { ok: false, reason: "UNSUPPORTED_ALGORITHM" };

  // 5. Decode. Anything undecodable is malformed, never "empty so skip".
  const payloadBytes = decodeBase64(attestation.payload);
  const signatureBytes = decodeBase64(attestation.signature);
  if (!payloadBytes || !signatureBytes || signatureBytes.length === 0) {
    return { ok: false, reason: "MALFORMED_ATTESTATION" };
  }

  const payloadJson = decodeBase64Text(attestation.payload, 4096);
  if (!payloadJson) return { ok: false, reason: "MALFORMED_ATTESTATION" };
  let payload: AttestationPayload;
  try {
    payload = JSON.parse(payloadJson) as AttestationPayload;
  } catch {
    return { ok: false, reason: "PAYLOAD_UNREADABLE" };
  }

  const boundAddress = displayText(payload?.address, 128);
  const boundNetwork = displayText(payload?.network, 64);
  const expiresAt = displayText(payload?.expiresAt, 40);
  if (!boundAddress || !boundNetwork || !expiresAt) return { ok: false, reason: "PAYLOAD_UNREADABLE" };

  // 6. Binding. Compare the decoded address byte-for-byte with the one about to
  //    be rendered, and the network with the one the payer chose. This is what
  //    makes a genuinely-signed attestation for a *different* address useless.
  if (boundAddress !== input.address || boundNetwork !== input.network) {
    return { ok: false, reason: "BINDING_MISMATCH" };
  }

  // 7. Freshness.
  const expiry = Date.parse(expiresAt);
  if (!Number.isFinite(expiry) || expiry <= now) return { ok: false, reason: "EXPIRED" };

  // 8. The signature itself. A verifier that cannot run has already returned
  //    false above; here a rejection is the only other outcome.
  let verified = false;
  try {
    verified = await verifier.verify({
      algorithm: pinned.algorithm,
      publicKeyBase64: pinned.publicKey,
      payload: payloadBytes,
      signature: signatureBytes,
    });
  } catch {
    verified = false;
  }
  if (!verified) return { ok: false, reason: "SIGNATURE_INVALID" };

  return { ok: true, address: { address: input.address, checksum: addressChecksum(input.address) }, keyId };
}

/** Convenience wrapper for the address response the page has already mapped. */
export function verifyAddressView(
  view: PayAddressView,
  network: string,
  options: Omit<VerifyAddressInput, "address" | "network" | "attestation"> = {},
): Promise<AttestationVerdict> {
  return verifyAddressAttestation({
    address: view.address,
    network,
    attestation: view.attestation,
    ...options,
  });
}
